import React from 'react';
import styled from 'styled-components';

import useLanguage from '@/components/common/hooks/useLanguage';
import { formatMoney, formatSigned } from '@/utils/money';

// The raises this team actually gives. One edit here changes every quick action.
export const SALARY_INCREMENTS = [300, 500];

interface Props {
  current: number | undefined;
  onPick: (amount: number, comment: string) => void;
}

/**
 * A raise is nearly always "the current salary plus a round number", so each
 * button shows both the step and where it lands -- the admin confirms the result
 * rather than doing the arithmetic.
 *
 * With no current salary there is nothing to add to, so the buttons stay hidden
 * and the amount field is the only way in.
 */
const SalaryQuickActions: React.FC<Props> = ({ current, onPick }) => {
  const [{ payout }] = useLanguage();

  if (current === undefined) return null;

  return (
    <>
      <Label>{payout.quick_actions}</Label>
      <Row>
        {SALARY_INCREMENTS.map((increment) => (
          <Button
            key={increment}
            onClick={() => onPick(current + increment, formatSigned(increment))}
          >
            <Step>{formatSigned(increment)}</Step>
            <Result>{formatMoney(current + increment)}</Result>
          </Button>
        ))}
      </Row>
    </>
  );
};

const Label = styled.div`
  text-transform: uppercase;
  font-size: 12px;
  font-weight: bold;
  letter-spacing: 1.3px;
  color: #909599;
  margin: 16px 0 8px;
`;

const Row = styled.div`
  display: flex;
`;

const Button = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 10px 8px;
  margin-right: 8px;
  border-radius: 6px;
  border: solid 1px #dae1e8;
  background: #ffffff;
  cursor: pointer;
  user-select: none;

  :last-child {
    margin-right: 0;
  }
`;

const Step = styled.div`
  font-size: 16px;
  font-weight: 800;
  color: #188038;
`;

const Result = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #909599;
  margin-top: 2px;
`;

export default SalaryQuickActions;
